import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { faqs } from "@/mocks/faqs";
import PageHeader from "@/components/feature/PageHeader";

export default function FaqPage() {
  const [open, setOpen] = useState<number>(0);

  const groups = useMemo(() => {
    const map = new Map<string, typeof faqs>();
    faqs.forEach((f) => {
      if (!map.has(f.group)) map.set(f.group, []);
      map.get(f.group)!.push(f);
    });
    return Array.from(map.entries());
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <PageHeader
        title="常见问题"
        subtitle="자주 묻는 질문 · FAQ"
        desc="关于韩国免费漫画的资源入口、日更无遮拦、网页与APP使用等常见疑问，这里都给出了清晰的解答。如果还是没找到答案，欢迎联系我们。"
        crumbs={[{ label: "常见问题" }]}
      />

      <section className="mx-auto max-w-4xl px-4 md:px-6 py-8 md:py-12">
        <div className="space-y-8">
          {groups.map(([group, list]) => (
            <div key={group}>
              <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                {group}
              </h2>
              <div className="space-y-3">
                {list.map((f) => {
                  const idx = faqs.findIndex((x) => x.id === f.id);
                  const isOpen = open === idx;
                  return (
                    <div key={f.id} className="rounded-lg bg-background-100 border border-background-200/70 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setOpen(isOpen ? -1 : idx)}
                        className="w-full flex items-center justify-between gap-4 px-4 md:px-5 py-4 text-left"
                      >
                        <span className="font-heading text-sm text-foreground-950">{f.q}</span>
                        <span
                          className={`w-7 h-7 shrink-0 rounded-full bg-primary-100/60 text-primary-500 flex items-center justify-center transition-transform ${
                            isOpen ? "rotate-45" : ""
                          }`}
                        >
                          <i className="ri-add-line text-sm"></i>
                        </span>
                      </button>
                      {isOpen && (
                        <p className="px-4 md:px-5 pb-4 text-sm text-foreground-700 leading-relaxed">{f.a}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-xl bg-background-100 border border-background-200/70 p-6 flex flex-col sm:flex-row items-center gap-4 justify-between">
          <p className="text-sm text-foreground-700">没有找到你想要的答案？</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 font-bold text-xs whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            <i className="ri-chat-3-line"></i> 联系我们
          </Link>
        </div>
      </section>
    </>
  );
}